import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const buttonVariants = cva(
  "group inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[10px] border text-sm font-semibold tracking-[0.01em] transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-kin-cream disabled:pointer-events-none disabled:opacity-45 disabled:shadow-none active:scale-[0.98]",
  {
    variants: {
      variant: {
        primary:
          "border-kin-green-dark/20 bg-kin-green text-white shadow-[0_4px_12px_-4px_rgba(52,199,89,0.4)] hover:opacity-90 focus-visible:ring-kin-green/50",
        secondary:
          "border-kin-olive/20 bg-kin-white/80 text-kin-body shadow-[0_2px_4px_rgba(0,0,0,0.02)] backdrop-blur-md hover:bg-kin-white hover:border-kin-olive/40 focus-visible:ring-kin-olive/30",
        accent:
          "border-kin-lavender/20 bg-kin-lavender text-white shadow-[0_4px_12px_-4px_rgba(175,82,222,0.4)] hover:opacity-90 focus-visible:ring-kin-lavender/50",
        ghost:
          "border-transparent bg-transparent text-kin-body shadow-none hover:bg-kin-olive/10 hover:text-kin-black focus-visible:ring-kin-olive/30",
        link: "border-transparent bg-transparent px-0 text-kin-green-dark shadow-none hover:text-kin-green focus-visible:ring-kin-green/35 focus-visible:ring-offset-0",
      },
      size: {
        large: "h-12 px-6",
        medium: "h-11 px-5",
        small: "h-9 px-4 text-xs",
        icon: "h-11 w-11 p-0",
      },
    },
    defaultVariants: { variant: "primary", size: "medium" },
    compoundVariants: [{ variant: "link", className: "h-auto p-0" }],
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    <button
      type={type}
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  ),
);
Button.displayName = "Button";

export { Button };
